import { Batiment, Vehicule } from '../../../shared/types/game';
import { isCtaBuildingType, unitTypeLabel, vehicleStateLabel } from './mapUtils';

/**
 * Construction des lignes « unités disponibles ».
 *
 * Extrait de `useMapMetrics` (Étape 2b) : le calcul dépendait des filtres, ce qui
 * obligeait à hisser ceux-ci dans `Map.tsx` — d'où la fuite de filtres d'une vue à
 * l'autre (diagnostic n°4 du prototype). En helper PUR, chaque vue peut garder ses
 * filtres en état LOCAL et fabriquer ses propres lignes.
 */

export interface AvailableUnitRow {
  id: number;
  unit: string;
  type: string;
  service: string;
  crew: string;
  state: string;
  eta: string;
}

export interface UnitsFilters {
  buildingFilter: number | 'all';
  vehicleTypeFilter: string;
  unitsFilter: 'all' | 'disponible' | 'en_mission' | 'en_maintenance';
  unitsSearch: string;
}

export const DEFAULT_UNITS_FILTERS: UnitsFilters = {
  buildingFilter: 'all',
  vehicleTypeFilter: 'all',
  unitsFilter: 'all',
  unitsSearch: '',
};

export function buildAvailableUnitsRows(
  playerVehicles: Vehicule[],
  buildings: Batiment[],
  filters: UnitsFilters,
  limit = 24,
): AvailableUnitRow[] {
  const normalizedSearch = filters.unitsSearch.trim().toLowerCase();
  const normalizedTypeFilter = filters.vehicleTypeFilter.trim().toLowerCase();

  return playerVehicles
    .filter((v) => {
      const linkedBuilding = buildings.find((b) => Number(b.id) === Number(v.batiment_id));
      if (filters.buildingFilter === 'all') return !isCtaBuildingType(linkedBuilding?.type);
      return Number(v.batiment_id) === Number(filters.buildingFilter);
    })
    .filter((v) =>
      normalizedTypeFilter === 'all'
        ? true
        : String(v.type || '').trim().toLowerCase() === normalizedTypeFilter,
    )
    .filter((v) => (filters.unitsFilter === 'all' ? true : String(v.statut) === filters.unitsFilter))
    .filter((v) => {
      if (!normalizedSearch) return true;
      return (
        String(v.nom || '').toLowerCase().includes(normalizedSearch) ||
        String(v.type || '').toLowerCase().includes(normalizedSearch)
      );
    })
    .slice(0, limit)
    .map((v, idx) => {
      const state = vehicleStateLabel(v.statut);
      const building = buildings.find((b) => Number(b.id) === Number(v.batiment_id));
      const service = building ? unitTypeLabel(building.type) : 'Non assigne';
      const crewTotal = Math.max(2, Number(v.capacite || 2));
      const crewDispo =
        state === 'PRÊT' ? Math.max(1, crewTotal - (idx % 2)) : Math.max(0, crewTotal - 2);
      return {
        id: v.id,
        unit: v.nom || `Unité ${idx + 1}`,
        type: String(v.type || unitTypeLabel(building?.type || '')),
        service,
        crew: `${crewDispo}/${crewTotal}`,
        state,
        eta: state === 'PRÊT' ? `${2 + (idx % 5)} min` : '—',
      };
    });
}
